<script lang="ts">
  import { untrack } from 'svelte';
  import { api } from '../../api/client';
  import Modal from '../Modal.svelte';
  import {
    calendar, fromStored, goalMeasures, goalPeriods, MEASURE_NAMES, PERIOD_NAMES, targetUnit, toStored,
    type Goal, type GoalMeasure, type GoalPeriodKind,
  } from '../../utils/insights';
  import { GROUPS, SPORTS } from '../../utils/sports';
  import { t } from '../../i18n/index.ts';

  // GoalEditor adds a goal, or with goal changes its target (spec 006
  // 006 FR-062, 006 FR-066). A goal's scope, measure, and period are fixed once made.
  let { goal, onsaved, onclose }: { goal?: Goal; onsaved: (g: Goal) => void; onclose: () => void } = $props();

  // The form starts from the goal as it was when opened.
  const start = untrack(() => goal);
  let sport = $state(start?.sport ?? '');
  let measure = $state<GoalMeasure>(start?.measure ?? 'distance');
  let period = $state<GoalPeriodKind>(start?.period ?? 'week');
  let typed = $state(start ? String(fromStored(start.measure, start.target)) : '');
  let error = $state('');
  let saving = $state(false);

  // Steps and active minutes count every sport, and only steps are daily.
  $effect(() => {
    if (!goalMeasures(sport).includes(measure)) measure = goalMeasures(sport)[0];
  });
  $effect(() => {
    if (!goalPeriods(measure).includes(period)) period = 'week';
  });

  async function save(e: SubmitEvent) {
    e.preventDefault();
    const n = Number(typed);
    if (!(n > 0)) {
      error = t('activitiesGoalTargetPositive');
      return;
    }
    saving = true;
    try {
      const target = toStored(measure, n);
      const got = goal
        ? await api.patch<Goal>(`/me/goals/${goal.id}?${calendar()}`, { target })
        : await api.post<Goal>(`/me/goals?${calendar()}`, { sport: measure === 'steps' || measure === 'active_minutes' ? '' : sport, measure, period, target });
      onsaved(got);
    } catch (err: any) {
      error = err.message;
    } finally {
      saving = false;
    }
  }
</script>

<Modal title={goal ? t('activitiesGoalEditTitle') : t('activitiesGoalAddTitle')} {onclose}>
  <form class="space-y-4" onsubmit={save}>
    {#if !goal}
      <label class="block text-sm">
        <span class="block mb-1">{t('activitiesSport')}</span>
        <select class="input w-full" bind:value={sport}>
          <option value="">{t('activitiesAllSports')}</option>
          {#each GROUPS as g (g.id)}
            <optgroup label={g.name}>
              {#each SPORTS.filter((s) => s.group === g.id) as s (s.id)}
                <option value={s.id}>{s.name}</option>
              {/each}
            </optgroup>
          {/each}
        </select>
      </label>
      <fieldset class="text-sm">
        <legend class="mb-1">{t('activitiesGoalMeasure')}</legend>
        <div class="flex flex-wrap gap-2">
          {#each goalMeasures(sport) as m (m)}
            <button type="button" class="btn btn-sm {measure === m ? 'btn-primary' : 'btn-secondary'}" aria-pressed={measure === m} onclick={() => (measure = m)}>{MEASURE_NAMES[m]}</button>
          {/each}
        </div>
      </fieldset>
      <fieldset class="text-sm">
        <legend class="mb-1">{t('activitiesGoalEvery')}</legend>
        <div class="flex flex-wrap gap-2">
          {#each goalPeriods(measure) as p (p)}
            <button type="button" class="btn btn-sm {period === p ? 'btn-primary' : 'btn-secondary'}" aria-pressed={period === p} onclick={() => (period = p)}>{PERIOD_NAMES[p]}</button>
          {/each}
        </div>
      </fieldset>
    {/if}
    <label class="block text-sm">
      <span class="block mb-1">{t('activitiesGoalTargetIn', { unit: targetUnit(measure) })}</span>
      <input class="input w-full" type="number" min="0" step="any" inputmode="decimal" bind:value={typed} required />
    </label>
    {#if goal}
      <p class="text-xs text-text-muted">{t('activitiesGoalTargetFrom', { period: goal.period })}</p>
    {/if}
    {#if error}
      <p class="text-sm" style="color: var(--theme-error);">{error}</p>
    {/if}
    <div class="flex justify-end gap-2">
      <button type="button" class="btn btn-secondary" onclick={onclose}>{t('appCancel')}</button>
      <button type="submit" class="btn btn-primary" disabled={saving}>{goal ? t('appSave') : t('activitiesGoalsAdd')}</button>
    </div>
  </form>
</Modal>
